import { DEVELOPMENT_SPECS, WEAPON_SPECS } from './constants.js';
/** The weapons that cross from one cluster into another, with Common Cause. */
export const REGIONAL_WEAPONS = ['ballistic', 'recon_drone'];
export function hasExtendedRange(city) {
    return city.development.some((r) => r.id === 'command_1' && r.done);
}
/** Whether both sides still have a city standing in this cluster. */
export function clusterActive(cities, cluster) {
    if (cluster < 0)
        return false;
    let west = false;
    let east = false;
    for (const c of cities) {
        if (c.cluster !== cluster || c.defeated)
            continue;
        if (c.faction === 'WEST')
            west = true;
        else
            east = true;
        if (west && east)
            return true;
    }
    return false;
}
/** Where a city stands this stage: in a live cluster, a decided one, the reserve, or out. */
export function cityState(city, cities) {
    if (city.defeated)
        return 'FALLEN';
    if (city.cluster < 0)
        return 'RESERVE';
    return clusterActive(cities, city.cluster) ? 'ACTIVE' : 'RESOLVED';
}
/** The scope a strike on this target would be, whether or not it is allowed. */
export function scopeOf(attacker, target, weapon) {
    if (weapon === 'icbm')
        return 'GLOBAL';
    if (attacker.cluster >= 0 && target.cluster === attacker.cluster)
        return 'LOCAL';
    if (attacker.region >= 0 && target.region === attacker.region)
        return 'REGIONAL';
    return null;
}
/**
 * Whether this attacker may send this weapon at this target, and in which
 * scope. Distance is checked separately, on the route (`planRoute`): this is
 * who is on the list, the route is whether the flight fits.
 *
 * `cities` is everyone on the board as the caller sees them — cluster, region
 * and whether they have fallen are public — and decides which clusters are
 * still being fought.
 */
export function strikeScope(attacker, target, weapon, cities) {
    const label = WEAPON_SPECS[weapon]?.label ?? weapon;
    const scope = scopeOf(attacker, target, weapon);
    if (target.faction === attacker.faction)
        return { ok: false, scope, reason: 'you cannot strike your own side' };
    if (target.defeated)
        return { ok: false, scope, reason: `${target.name} is already out of the fight` };
    if (weapon === 'icbm')
        return { ok: true, scope: 'GLOBAL' };
    if (attacker.cluster < 0) {
        return { ok: false, scope, reason: 'a city in strategic reserve has no front: only an ICBM leaves it' };
    }
    if (!clusterActive(cities, attacker.cluster)) {
        return { ok: false, scope, reason: 'your cluster is decided: until the board re-forms, only an ICBM leaves it' };
    }
    if (scope === 'LOCAL')
        return { ok: true, scope };
    if (scope === null) {
        return { ok: false, scope, reason: `${target.name} is in another region: only an ICBM reaches that far` };
    }
    if (!REGIONAL_WEAPONS.includes(weapon)) {
        return { ok: false, scope, reason: `a ${label} stays inside its own cluster: only a ballistic missile with ${DEVELOPMENT_SPECS.command_1.label} crosses to another` };
    }
    if (!hasExtendedRange(attacker)) {
        return { ok: false, scope, reason: `${target.name} is in another cluster: reaching it takes ${DEVELOPMENT_SPECS.command_1.label}` };
    }
    return { ok: true, scope };
}
/** How a scope reads to a commander. */
export const SCOPE_LABEL = {
    LOCAL: 'Local',
    REGIONAL: `Regional — ${DEVELOPMENT_SPECS.command_1.label}`,
    GLOBAL: 'Global — ICBM',
};
//# sourceMappingURL=scope.js.map